import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, BotonSuave, Cargando, Hora } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 1 (citas): el horario DEL CENTRO. Es el marco dentro del cual se
 * ofrecen huecos: con todos los días cerrados, el widget no da ni una hora
 * aunque haya servicios y profesionales.
 *
 * Se guarda en `PUT /appointment-settings`, que es un upsert parcial: mandando
 * solo `weeklySchedule` el resto de ajustes (duración, antelación, avisos…)
 * se quedan como están, o nacen con sus valores por defecto si es la primera
 * vez. La pantalla completa de Horarios sigue siendo la de siempre; aquí solo
 * están los días y las horas, que es lo que bloquea.
 */

const DIAS = [
  { key: "1", corto: "Lun", largo: "Lunes" },
  { key: "2", corto: "Mar", largo: "Martes" },
  { key: "3", corto: "Mié", largo: "Miércoles" },
  { key: "4", corto: "Jue", largo: "Jueves" },
  { key: "5", corto: "Vie", largo: "Viernes" },
  { key: "6", corto: "Sáb", largo: "Sábado" },
  { key: "0", corto: "Dom", largo: "Domingo" },
] as const;

interface Turno {
  startTime: string;
  endTime: string;
}
interface Dia {
  isOpen: boolean;
  shifts: Turno[];
}
type Semana = Record<string, Dia>;

const MAX_TURNOS = 3;

/** Horario de partida si el negocio no tiene nada guardado todavía. */
function semanaPorDefecto(): Semana {
  const semana: Semana = {};
  for (const dia of DIAS) {
    const laborable = dia.key !== "0" && dia.key !== "6";
    semana[dia.key] = {
      isOpen: laborable,
      shifts: laborable
        ? [
            { startTime: "09:00", endTime: "14:00" },
            { startTime: "16:00", endTime: "20:00" },
          ]
        : [],
    };
  }
  return semana;
}

function minutos(hora: string): number {
  const [h, m] = hora.split(":").map(Number);
  return h * 60 + m;
}

export default function PasoHorarioCitas({
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [semana, setSemana] = useState<Semana | null>(null);
  const [guardando, setGuardando] = useState(false);

  // OJO: este GET crea la fila de ajustes si no existía (el endpoint es así de
  // siempre, igual que al abrir la pantalla de Horarios). No pasa nada: el paso
  // no se da por hecho por eso, sino por tener días abiertos con turnos.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await apiClient.get("/appointment-settings");
        const guardada = res.data?.data?.weeklySchedule as Semana | undefined;
        if (!vivo) return;
        const base = semanaPorDefecto();
        if (guardada && typeof guardada === "object") {
          for (const dia of DIAS) {
            const valor = guardada[dia.key];
            if (valor && typeof valor === "object") {
              base[dia.key] = {
                isOpen: valor.isOpen === true,
                shifts: Array.isArray(valor.shifts) ? valor.shifts : [],
              };
            }
          }
        }
        setSemana(base);
      } catch {
        // Si no se puede leer, se parte del horario de ejemplo: es mejor que
        // dejarle la pantalla vacía sin poder seguir.
        if (vivo) setSemana(semanaPorDefecto());
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const cambiarDia = (key: string, patch: Partial<Dia>) => {
    setSemana((prev) => (prev ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  };

  const cambiarTurno = (key: string, indice: number, patch: Partial<Turno>) => {
    setSemana((prev) => {
      if (!prev) return prev;
      const shifts = prev[key].shifts.map((t, i) =>
        i === indice ? { ...t, ...patch } : t,
      );
      return { ...prev, [key]: { ...prev[key], shifts } };
    });
  };

  const abrirCerrar = (key: string) => {
    setSemana((prev) => {
      if (!prev) return prev;
      const dia = prev[key];
      if (dia.isOpen) return { ...prev, [key]: { isOpen: false, shifts: [] } };
      return {
        ...prev,
        [key]: {
          isOpen: true,
          shifts:
            dia.shifts.length > 0
              ? dia.shifts
              : [{ startTime: "09:00", endTime: "14:00" }],
        },
      };
    });
  };

  /** Copia los turnos del primer día abierto a todos los demás días abiertos. */
  const copiarAlResto = () => {
    setSemana((prev) => {
      if (!prev) return prev;
      const origen = DIAS.find((d) => prev[d.key].isOpen && prev[d.key].shifts.length > 0);
      if (!origen) return prev;
      const turnos = prev[origen.key].shifts;
      const copia: Semana = { ...prev };
      for (const dia of DIAS) {
        if (copia[dia.key].isOpen) {
          copia[dia.key] = {
            isOpen: true,
            shifts: turnos.map((t) => ({ ...t })),
          };
        }
      }
      return copia;
    });
    showToast("Mismo horario en todos los días abiertos");
  };

  const guardar = useCallback(async (): Promise<boolean> => {
    if (!semana) return false;

    const abiertos = DIAS.filter((d) => semana[d.key].isOpen);
    if (abiertos.length === 0) {
      showToast("Marca al menos un día abierto: si no, no se puede pedir cita", "error");
      return false;
    }
    for (const dia of abiertos) {
      const turnos = semana[dia.key].shifts;
      if (turnos.length === 0) {
        showToast(`${dia.largo}: añade un horario o cierra el día`, "error");
        return false;
      }
      for (const [i, turno] of turnos.entries()) {
        if (minutos(turno.endTime) <= minutos(turno.startTime)) {
          showToast(`${dia.largo}: la hora de cierre debe ser posterior a la de apertura`, "error");
          return false;
        }
        for (let j = 0; j < i; j++) {
          const otro = turnos[j];
          if (
            minutos(turno.startTime) < minutos(otro.endTime) &&
            minutos(otro.startTime) < minutos(turno.endTime)
          ) {
            showToast(`${dia.largo}: los horarios se solapan`, "error");
            return false;
          }
        }
      }
    }

    try {
      setGuardando(true);
      await apiClient.put("/appointment-settings", { weeklySchedule: semana });
      refrescar();
      showToast("Horario guardado");
      return true;
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar el horario"),
        "error",
      );
      return false;
    } finally {
      setGuardando(false);
    }
  }, [semana, refrescar, showToast]);

  useEffect(() => {
    registrarAvance(guardar);
    registrarTextoAvance(guardando ? "Guardando…" : "Guardar y continuar");
    return () => {
      registrarAvance(null);
      registrarTextoAvance(null);
    };
  }, [guardar, guardando, registrarAvance, registrarTextoAvance]);

  if (!semana) return <Cargando texto="Cargando tu horario…" />;

  return (
    <div className="space-y-4">
      <Aviso>
        Estas son las horas en las que tu centro está abierto. Tus clientes solo
        podrán pedir cita dentro de ellas, así que si un día está cerrado, no se
        ofrecerá ninguna hora.
      </Aviso>

      <div className="flex justify-end">
        <BotonSuave onClick={copiarAlResto}>
          Poner el mismo horario todos los días
        </BotonSuave>
      </div>

      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {DIAS.map((dia) => {
          const valor = semana[dia.key];
          return (
            <div key={dia.key} className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={valor.isOpen}
                    onClick={() => abrirCerrar(dia.key)}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                      valor.isOpen ? "bg-primary-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        valor.isOpen ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-gray-900">
                    {dia.largo}
                  </span>
                </div>
                {!valor.isOpen && (
                  <span className="text-xs text-gray-400">Cerrado</span>
                )}
              </div>

              {valor.isOpen && (
                <div className="mt-2 pl-12 space-y-2">
                  {valor.shifts.map((turno, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap">
                      <Hora
                        ariaLabel={`${dia.largo}: hora de apertura`}
                        value={turno.startTime}
                        onChange={(v) => cambiarTurno(dia.key, i, { startTime: v })}
                      />
                      <span className="text-gray-400 text-sm">a</span>
                      <Hora
                        ariaLabel={`${dia.largo}: hora de cierre`}
                        value={turno.endTime}
                        onChange={(v) => cambiarTurno(dia.key, i, { endTime: v })}
                      />
                      {valor.shifts.length > 1 && (
                        <BotonSuave
                          tono="peligro"
                          onClick={() =>
                            cambiarDia(dia.key, {
                              shifts: valor.shifts.filter((_, j) => j !== i),
                            })
                          }
                        >
                          Quitar
                        </BotonSuave>
                      )}
                    </div>
                  ))}
                  {valor.shifts.length < MAX_TURNOS && (
                    <BotonSuave
                      onClick={() =>
                        cambiarDia(dia.key, {
                          shifts: [
                            ...valor.shifts,
                            { startTime: "16:00", endTime: "20:00" },
                          ],
                        })
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Añadir otro tramo (mañana y tarde)
                      </span>
                    </BotonSuave>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
